import { Axios } from "axios";
import { Action, CombinedState, Store } from "redux";
import { ThunkDispatch } from "redux-thunk";

import {
    $CheckoutCustomerDetailsFormModuleName,
    TCheckoutCustomerDetailsFormState,
} from "@redux/modules/value/checkout/customerDetails/checkoutCustomerDetailsForm.types";
import {
    $PaymentMethodsFormModuleName,
    TPaymentMethodsFormState,
} from "@redux/modules/value/checkout/paymentMethodsForm/paymentMethodsForm.types";
import {
    $ScrollLockModuleName,
    TScrollLockState,
} from "@redux/modules/value/scrollLock/scrollLock.types";
import {
    $SearchResultsModuleName,
    TSearchResultsState,
} from "@redux/modules/value/searchResults/searchResults.types";

import { $AddressModuleName } from "./modules/api/data/address/address.types";
import {
    $AddressBookModuleName,
    TAddressBookState,
} from "./modules/api/data/addressbook/addressbook.types";
import {
    $BoxCountModuleName,
    TBoxCounterState,
} from "./modules/api/data/boxCount/boxCount.types";
import {
    $CartModuleName,
    TCartDataSlice,
} from "./modules/api/data/cart/cart.types";
import {
    $CheckUserModuleName,
    TCheckUserState,
} from "./modules/api/data/checkUser/checkUser.types";
import {
    $PaymentModuleName,
    TPaymentState,
} from "@redux/modules/api/data/checkout/payment/payment.types";
import {
    $PaymentMethodsModuleName,
    TPaymentMethodsState,
} from "./modules/api/data/checkout/paymentMethods/paymentMethods.types";
import {
    $BillingCountriesModuleName,
    TBillingCountriesState,
} from "./modules/api/data/countries/billing/billingCountries.types";
import {
    $CustomerModuleName,
    TCustomerState,
} from "./modules/api/data/customer/customer.types";
import {
    $OrderDetailModuleName,
    TOrderDetailsState,
} from "./modules/api/data/orderDetail/orderDetail.types";
import {
    $PhonenumberPrefixesModuleName,
    TPhonenumberPrefixesState,
} from "./modules/api/data/phonenumberPrefixes/phonenumberPrefixes.types";
import {
    $PreferredStoreModuleName,
    TPreferredStoreState,
} from "./modules/api/data/preferredStore/preferredStore.types";
import {
    $SearchStoresModuleName,
    TSearchStoresState,
} from "./modules/api/data/stores/searchStores/searchStores.types";
import {
    $StoreDetailsModuleName,
    TStoreDetailsState,
} from "./modules/api/data/stores/storeDetails/storeDetails.types";
import {
    $StorelocatorModuleName,
    TStoreLocatorState,
} from "./modules/api/data/stores/storeLocator/storeLocator.types";
import {
    $StoresModuleName,
    TStoresState,
} from "./modules/api/data/stores/stores.types";
import {
    $VatPrefixesModuleName,
    TVatPrefixesState,
} from "./modules/api/data/vatPrefixes/vatPrefixes.types";
import {
    $WishlistModuleName,
    TWishlistState,
} from "./modules/api/data/wishlist/wishlist.types";
import {
    $ApiErrorsModuleName,
    TApiErrorsState,
} from "./modules/api/errors/errors.types";
import {
    $LoadingModuleName,
    TApiLoadingState,
} from "./modules/api/loading/loading.types";
import {
    $AccountFormsModuleName,
    TAccountFormsState,
} from "./modules/value/accountForms/accountForms.types";
import {
    $AddToCartModuleName,
    TAddToCartState,
} from "./modules/value/addToCart/addToCart.types";
import {
    $ApiMessagesModuleName,
    TApiMessagesState,
} from "./modules/value/apiMessages/apiMessages.types";
import {
    $ErrorsModuleName,
    TErrorsState,
} from "./modules/value/errors/errors.types";
import {
    $FlyoutCategoriesModuleName,
    FlyoutCategory,
} from "./modules/value/flyoutCategories/flyoutCategories.types";
import {
    $HasAccountModalUserDataModuleName,
    THasAccountModalUserDataState,
} from "./modules/value/hasAccountModalUserData/hasAccountModalUserData.types";
import {
    $HeaderModuleName,
    THeaderState,
} from "./modules/value/header/header.types";
import {
    $ImageViewerModuleName,
    TImageViewerState,
} from "./modules/value/imageViewer/imageViewer.types";
import {
    $LocalesModuleName,
    TLocalesState,
} from "./modules/value/locales/locales.types";
import {
    $MessagesModuleName,
    TMessagesState,
} from "./modules/value/messages/messages.types";
import {
    $ProductComparisonSkuListModuleName,
    TProductComparisonSkuListState,
} from "./modules/value/productComparisonSkuList/productComparisonSkuList.types";
import {
    $ProductDetailModuleName,
    TProductDetailState,
} from "./modules/value/productDetail/productDetail.types";
import {
    $ProfileSidebarModuleName,
    TProfileSidebarState,
} from "./modules/value/profileSidebar/profileSidebar.types";
import {
    $SearchModuleName,
    SearchState,
} from "./modules/value/search/search.types";
import {
    $TranslationsModuleName,
    TTranslationsState,
} from "./modules/value/translations/translations.types";
import {
    $VatInfoModuleName,
    TVatInfoState,
} from "./modules/value/vatInfo/vatInfo.types";
import {
    $WishlistModalModuleName,
    TWishlistModalState,
} from "./modules/value/wishlistModal/wishlistModal.types";
import {
    $WishlistSidebarModuleName,
    TWishlistSidebarState,
} from "./modules/value/wishlistSidebar/wishlistSidebar.types";
import createReducerManager from "./reducerManager";

export const $DataModuleName = "data";
export const $ValueModuleName = "value";
export const $ApiModuleName = "api";

export type TStore = {
    reducerManagers: {
        [$ApiModuleName]: {
            [$DataModuleName]: ReturnType<
                typeof createReducerManager<IApiDataState, TApiAction<any>>
            >;
        };
        [$ValueModuleName]: ReturnType<
            typeof createReducerManager<IValueState, TValueAction<any>>
        >;
    };
} & Store<CombinedState<IState>, Action<any>> & {
    dispatch: ThunkDispatch<IState, TExtraThunkArgs, Action<any>>;
};

export type TApiDataStateSlice<T = any> = {
    error?: any;
    data?: T | null;
    params?: any | null;
};

export interface IApiDataState extends Partial<IRootApiDataState> { }

export type TExtraThunkArgs = Axios;

export interface IApiState {
    [$LoadingModuleName]: TApiLoadingState;
    [$ApiErrorsModuleName]: TApiErrorsState;
    [$DataModuleName]: IApiDataState;
}

export interface IState {
    [$ApiModuleName]: IApiState;
    [$ValueModuleName]: IValueState;
}

export interface IValueState extends Partial<IRootValueState> { }

interface IRootValueState {
    [$AccountFormsModuleName]: TAccountFormsState;
    [$TranslationsModuleName]: TTranslationsState;
    [$ProductDetailModuleName]: TProductDetailState;
    [$FlyoutCategoriesModuleName]: FlyoutCategory;
    [$MessagesModuleName]: TMessagesState;
    [$HeaderModuleName]: THeaderState;
    [$ApiMessagesModuleName]: TApiMessagesState;
    [$VatInfoModuleName]: TVatInfoState;
    [$ProfileSidebarModuleName]: TProfileSidebarState;
    [$LocalesModuleName]: TLocalesState;
    [$ImageViewerModuleName]: TImageViewerState;
    [$PaymentMethodsFormModuleName]: TPaymentMethodsFormState;
    [$HasAccountModalUserDataModuleName]: THasAccountModalUserDataState;
    [$CheckoutCustomerDetailsFormModuleName]: TCheckoutCustomerDetailsFormState;
    [$ErrorsModuleName]: TErrorsState;
    [$ProductComparisonSkuListModuleName]: TProductComparisonSkuListState;
    [$WishlistModalModuleName]: TWishlistModalState;
    [$WishlistSidebarModuleName]: TWishlistSidebarState;
    [$AddToCartModuleName]: TAddToCartState;
    [$SearchModuleName]: SearchState;
    [$SearchResultsModuleName]: TSearchResultsState;
    [$ScrollLockModuleName]: TScrollLockState;
}

interface IRootApiDataState {
    [$AddressBookModuleName]: TApiDataStateSlice<TAddressBookState>;
    [$AddressModuleName]: TApiDataStateSlice<TAddressBookState>;
    [$BillingCountriesModuleName]: TApiDataStateSlice<TBillingCountriesState>;
    [$BoxCountModuleName]: TBoxCounterState;
    [$CartModuleName]: TCartDataSlice;
    [$PaymentMethodsModuleName]: TApiDataStateSlice<TPaymentMethodsState>;
    [$PaymentMethodsModuleName]: TApiDataStateSlice<TPaymentMethodsState>;
    [$PaymentModuleName]: TPaymentState;
    [$CheckUserModuleName]: TApiDataStateSlice<TCheckUserState>;
    [$CustomerModuleName]: TApiDataStateSlice<TCustomerState>;
    [$OrderDetailModuleName]: TApiDataStateSlice<TOrderDetailsState>;
    [$PreferredStoreModuleName]: TApiDataStateSlice<TPreferredStoreState>;
    [$SearchStoresModuleName]: TApiDataStateSlice<TSearchStoresState>;
    [$StoreDetailsModuleName]: TApiDataStateSlice<TStoreDetailsState>;
    [$StorelocatorModuleName]: TApiDataStateSlice<TStoreLocatorState>;
    [$StoresModuleName]: TApiDataStateSlice<TStoresState>;
    [$VatPrefixesModuleName]: TApiDataStateSlice<TVatPrefixesState>;
    [$WishlistModuleName]: TApiDataStateSlice<TWishlistState>;
    [$PhonenumberPrefixesModuleName]: TApiDataStateSlice<TPhonenumberPrefixesState>;
}

export type TActionOptions = {
    skipSuccessData?: () => boolean;
    discriminator?: string;
    skipErrors?: Array<number>;
    mergeParams?: boolean;
};

export type TValueAction<T = any> = Action<string> & {
    payload: T;
};

export type TApiAction<T = any> = Action<string> & {
    payload: T;
    actionOptions: TActionOptions;
};
